import { Container } from "@mui/system"
import DatatableFooter from "../../../components/Misc/DataTable/DatatableFooter"
import { useSnackbar } from "notistack"
import { useState, useContext, useCallback, useEffect } from "react"
import { APIsContext } from "../../../Store/APIs/context"
import { NavigationContext } from "../../../Store/Navigation/context"
import { setLoading } from "../../../Store/Navigation/reducer"
import { AccountModel } from "neurotec-megamatcherid-management-client"
import { AccountRole, AccountsFieldOrderBy, IAccountFilter } from "../../../Store/Accounts/types"
import DataTable, { HeadCell, IDataTableId } from "../../../components/Misc/DataTable/DataTable"
import { FieldsOrder } from "../../../Store/Subjects/types"
import AccountsFilter from "../../../components/Filter/AccountsFilter"
import AddAccountDialog from "../../../components/Dialogs/AddAccountDialog"
import DeleteAccountDialog from "../../../components/Dialogs/DeleteAccountDialog"
import ChangeAccountDialog from "../../../components/Dialogs/ChangeAccontDialog"

const headCells: HeadCell[] = [
    {
        id: 'USERNAME',
        align: 'left',
        disablePadding: false,
        label: 'Username',
        width: "20%"
    },
    {
        id: 'ROLES',
        align: 'left',
        disablePadding: false,
        label: 'Role',
        width: "10%"
    },
    {
        id: 'UUID',
        align: 'left',
        disablePadding: false,
        label: 'UUID',
        width: "40%"
    },
    {
        id: 'NONE_CHANGE',
        align: 'right',
        disablePadding: false,
        label: '',
        width: "15%"
    },
    {
        id: 'NONE_DELETE',
        align: 'right',
        disablePadding: false,
        label: '',
        width: "15%"
    },
];

const Accounts: React.FC = () => {

    const { enqueueSnackbar } = useSnackbar();
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(20)
    const navigationDispatch = useContext(NavigationContext).dispatch
    const apisState = useContext(APIsContext).state
    const [accounts, setAccounts] = useState<AccountModel[]>([])
    const [accountsFilter, setAccountsFilter] = useState<IAccountFilter>({fieldsOrder: FieldsOrder.DESC, accountFieldToOrderBy: AccountsFieldOrderBy.USERNAME})
    const [accountCount, setAccountCount] = useState<number | undefined>(undefined)
    const [openCreateAccountDialog, setOpenCreateAccountDialog] = useState<boolean>(false)
    const [selected, setSelected] = useState<AccountModel | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [changeDialogOpen, setChangeDialogOpen] = useState(false)

    const fetchAccounts = useCallback(async () => {
        try {
            navigationDispatch(setLoading(true))
            apisState.AccountsApi.getAccounts({page: page, size: perPage, ...accountsFilter}).then(res => {
                setAccounts(res.data)
                navigationDispatch(setLoading(false))
            }).catch(e => {
                console.log(e)
                enqueueSnackbar(e.message, {variant: "error"})
                navigationDispatch(setLoading(false))
            })

            apisState.AccountsApi.getAccountsCount(accountsFilter).then(res => {
                setAccountCount(res.data as unknown as number)
            }).catch(e => {
                setAccountCount(0)
                console.log(e)
                enqueueSnackbar(e.message, {variant: "error"})
            })
            
        } catch (error: any) {
            console.log(error)
            enqueueSnackbar(error.message, {variant: "error"})
        }
    }, [page, perPage, accountsFilter, enqueueSnackbar, navigationDispatch, apisState.AccountsApi])

    useEffect(() => {
        fetchAccounts()
    }, [apisState.AccountsApi, accountsFilter, page, perPage, fetchAccounts, navigationDispatch])

    const handlePageChange = (event: React.ChangeEvent<unknown> | undefined, newPage: number) => {
        setPage(newPage)
    }

    const handleFilterChange = (filter: IAccountFilter) => {
        setPage(1)
        setAccountCount(undefined)
        setAccountsFilter(filter)
    }

    const handleDelete = (id: IDataTableId) => {
        const account = accounts.find(e => e.username === id.uuid)
        if (account === undefined) {
            enqueueSnackbar("Account not found", {variant: "error"})
            return
        }
        setSelected(account)
        setDeleteDialogOpen(true)
    }

    const handleChange = (id: IDataTableId) => {
        const account = accounts.find(e => e.username === id.uuid)
        if (account === undefined) {
            enqueueSnackbar("Account not found", {variant: "error"})
            return
        }
        setSelected(account)
        setChangeDialogOpen(true)
    }

    const handleAddAccountDialogOpen = (bool: boolean) => {
        setOpenCreateAccountDialog(bool)
    }

    return (
        <>
        <Container maxWidth="xl" sx={{ display: "flex", flexDirection: "column" }}>
            <AccountsFilter
                filter={accountsFilter}
                onFilterApply={handleFilterChange}
                setOpenCreateAccountDialog={handleAddAccountDialogOpen}
            />
            <DataTable
                data={accounts}
                filter={accountsFilter}
                onFilterChange={handleFilterChange}
                headCells={headCells}
                title="Accounts"
                type="accounts"
                onDelete={handleDelete}
                onChange={handleChange}
            />
            <DatatableFooter
                page={page}
                handlePageChange={handlePageChange}
                count={accountCount}
                perPage={perPage}
                setPerPage={setPerPage}
                label='Accounts'
            />
        </Container>
        <AddAccountDialog
            open={openCreateAccountDialog}
            onClose={handleAddAccountDialogOpen}
            rerender={fetchAccounts}
        />
        <DeleteAccountDialog
            username={selected?.username ?? ""}
            askForPassword={selected?.role ? (selected?.role === AccountRole.ADMIN? true : false) : false}
            open={deleteDialogOpen}
            setOpen={(open) => setDeleteDialogOpen(open)}
            rerender={() => fetchAccounts()}
        />
        <ChangeAccountDialog
            open={changeDialogOpen}
            account={selected}
            setOpen={(open) => setChangeDialogOpen(open)}
            rerender={fetchAccounts}
        />
        </>
    )
}

export default Accounts